/**
 * What an empty band does when nothing is transmitting.
 *
 * Chapter 2 gave every frequency its own static texture, which made the band *navigable* — a
 * given spot always sounds like itself. What it did not give the band is a sense of being
 * outdoors. A shortwave noise floor is not a steady hiss: it swells and sinks as the ionosphere
 * moves, and it is punctuated by crackle from lightning hundreds of miles away. Both are the
 * sound of an enormous, indifferent sky between the player and whoever is transmitting, which is
 * the feeling this whole game is for.
 *
 * ## Fade is deterministic; crackle is not
 *
 * Propagation fade is a pure function of frequency and elapsed time, so it can be reasoned about
 * and tested, and so two players on one frequency hear the band breathe together.
 *
 * Sferics are genuinely random, because a predictable crackle stops sounding like weather within
 * about a minute. The randomness is injected rather than reached for, so the scheduling rules
 * below are still testable without a clock or a Web Audio context.
 */
import { audioContext } from './context.js';

/** Bounds of the fade, as a multiplier on the noise floor. */
const FADE_MIN = 0.62;
const FADE_MAX = 1.18;

/**
 * How long a full swell takes, in seconds.
 *
 * Slow enough to be felt rather than heard as an effect. Two periods that do not divide each
 * other are summed so the pattern does not visibly repeat inside a session.
 */
const FADE_PERIOD_A = 47;
const FADE_PERIOD_B = 113;

/**
 * The slow swell and sink of the noise floor.
 *
 * Frequency is folded in so that two nearby channels do not fade in lockstep — the band should
 * feel like a lot of independent paths, not one gate on everything.
 *
 * Returns a multiplier to apply to the static level.
 */
export function propagationFade(khz: number, seconds: number): number {
  const phase = khz * 0.017;
  const a = Math.sin((seconds / FADE_PERIOD_A) * Math.PI * 2 + phase);
  const b = Math.sin((seconds / FADE_PERIOD_B) * Math.PI * 2 + phase * 2.3);
  // Weighted so the long period dominates and the short one only ruffles it.
  const combined = (a * 0.38 + b * 0.62 + 1) / 2;
  return FADE_MIN + (FADE_MAX - FADE_MIN) * combined;
}

/**
 * Crackle density at a frequency, in bursts per second.
 *
 * Lightning static is worst at the bottom of the shortwave range and thins out higher up, which
 * is both true and useful: the low end of the dial sounds rougher and more hostile, so moving up
 * the band feels like moving somewhere calmer, without a word being said about it.
 */
export function sfericRate(khz: number): number {
  const t = Math.min(1, Math.max(0, (khz - 3000) / 5000));
  return 3.1 - 2.5 * t;
}

/**
 * Seconds until the next crackle.
 *
 * Exponentially distributed, which is what makes it sound like weather rather than a metronome:
 * bursts clump and then leave gaps. A uniform interval is immediately recognisable as a machine.
 *
 * `random` must return [0, 1). Clamped at both ends so a pathological draw cannot schedule a
 * burst in the same millisecond or push the next one past the end of the session.
 */
export function sfericGapSeconds(khz: number, random: () => number): number {
  const rate = sfericRate(khz);
  // -ln(U)/rate is the standard exponential draw; U is nudged off zero so the log is finite.
  const u = Math.max(random(), 1e-6);
  return Math.min(12, Math.max(0.01, -Math.log(u) / rate));
}

/** One crackle. */
export interface Sferic {
  /** How long the burst lasts. Real static clicks are very short. */
  durationMs: number;
  /** Peak level, 0–1. */
  level: number;
  /** Centre of the burst's colouration, in Hz. */
  toneHz: number;
}

/**
 * Shape one crackle.
 *
 * Most are small; a few are close and loud. Level rises as the *fourth* power of the draw, which
 * puts **75% of bursts under half strength and about 1 in 10 at a close strike** — the numbers
 * matter because at three bursts a second an even spread is not weather, it is a woodpecker, and
 * a loud crack only startles while it is rare.
 *
 * Duration and colour follow a gentler curve off the same draw, so the two stay correlated — a
 * close strike is louder *and* longer *and* rounder, as a real one is — without the character of
 * every quiet tick collapsing to the same minimum.
 */
export function sfericBurst(khz: number, random: () => number): Sferic {
  const u = random();
  const loudness = u ** 4;
  const size = u ** 1.5;
  return {
    durationMs: 4 + size * 46,
    level: 0.12 + loudness * 0.88,
    // Closer strikes read as lower and rounder; distant ones as a thin tick.
    toneHz: 2600 - size * 1500 + (khz - 3000) * 0.06,
  };
}

/* --- the emitter ------------------------------------------------------------------------------ */

/** Seconds of white noise held to slice crackle out of. One buffer serves every burst. */
const SOURCE_SECONDS = 0.5;

/**
 * Output gain applied to every burst.
 *
 * Not a taste setting — a correction, and it was found by measuring rather than by listening.
 * A burst commanded at full level came out of the colouring band-pass at about 0.3, and by the
 * time it had been through the noise duck, the static bus trim and the master it peaked around
 * 0.09 at the output. The static's *own* peaks sit near 0.2. So crackle was arriving roughly 6 dB
 * under the hiss it was supposed to be cracking through, and measured against the live output it
 * was indistinguishable from the noise floor: a paired A/B with the interference control at 100%
 * and 0% showed no difference at all.
 *
 * At 3x, a burst peaks near 0.28 against static peaks near 0.2 — audible as a transient, still
 * well under the mixer's limiter threshold.
 */
const BURST_GAIN = 3;

export interface SfericEmitterOptions {
  destination: AudioNode;
  /** Injectable for tests and for a deterministic replay if one is ever wanted. */
  random?: () => number;
}

/**
 * Plays crackle into the static path.
 *
 * Each burst is three nodes that live for under a tenth of a second and are then dropped — at the
 * busiest end of the band that is about three transient nodes per second against a graph of
 * around twenty permanent ones, which is well inside any sane budget. Nothing accumulates: the
 * buffer is shared, and a finished burst disconnects itself.
 */
export class SfericEmitter {
  private readonly destination: AudioNode;
  private readonly random: () => number;
  private buffer: AudioBuffer | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private khz = 5000;
  /** Scales every burst — the receiver ducks this under a strong signal. */
  private level = 1;
  private running = false;

  constructor(options: SfericEmitterOptions) {
    this.destination = options.destination;
    this.random = options.random ?? Math.random;
  }

  setFrequency(khz: number): void {
    this.khz = khz;
  }

  /**
   * How much crackle the player has asked for, 0–1.
   *
   * Not a duck: bursts are routed *into* the static path, so they already sink under a signal and
   * ride the propagation fade without being told to. This is the accessibility control — sudden
   * sharp transients are exactly the kind of thing a player may need less of, and turning them
   * down should not mean turning the band off.
   */
  setLevel(value: number): void {
    this.level = Math.max(0, Math.min(1, value));
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.schedule();
  }

  stop(): void {
    this.running = false;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private schedule(): void {
    if (!this.running) return;
    const gap = sfericGapSeconds(this.khz, this.random);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.fire();
      this.schedule();
    }, gap * 1000);
  }

  private fire(): void {
    if (!this.running || this.level <= 0.001) return;

    let ctx: AudioContext;
    try {
      ctx = audioContext();
    } catch {
      // No audio context: the band is simply quiet. Nothing here is worth an error.
      this.running = false;
      return;
    }
    if (ctx.state !== 'running') return;

    if (!this.buffer) {
      const frames = Math.floor(ctx.sampleRate * SOURCE_SECONDS);
      const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < frames; i += 1) data[i] = this.random() * 2 - 1;
      this.buffer = buffer;
    }

    const burst = sfericBurst(this.khz, this.random);
    const seconds = burst.durationMs / 1000;
    const now = ctx.currentTime;

    const source = ctx.createBufferSource();
    source.buffer = this.buffer;
    // Start somewhere random in the buffer so repeated bursts are not the same click.
    const offset = this.random() * (SOURCE_SECONDS - seconds - 0.01);

    const colour = ctx.createBiquadFilter();
    colour.type = 'bandpass';
    colour.frequency.value = burst.toneHz;
    colour.Q.value = 0.9;

    const gain = ctx.createGain();
    // A click, not a fade: near-instant attack, exponential-ish decay. Ramped from zero rather
    // than assigned, because a gain that steps *is* a click — and not the one we want.
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(burst.level * this.level * BURST_GAIN, now + 0.0015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + seconds);

    source.connect(colour).connect(gain).connect(this.destination);
    source.start(now, Math.max(0, offset), seconds + 0.02);
    source.onended = () => {
      source.disconnect();
      colour.disconnect();
      gain.disconnect();
    };
  }
}
